import {Injectable, Injector} from "@angular/core";
import {BaseService} from "@shared/services/base.service";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {Ticket} from "@shared/models/ticket";
import {Observable} from "rxjs";
import {catchError, map} from "@node_modules/rxjs/operators";

/**
 * Interface for commission inquiry data
 */
export interface CommissionInquiry {
    oneTimeSpiffCommissionTrueUp: number | null;
    oneTimeResidualGrossCommissionTrueUp: number | null;
    numberOfMonthsIncludedInTrueUp: number | null;
    netNewGrossCommission: number | null;
}

/**
 * Interface for commission inquiry input data
 */
export interface CommissionInquiryInput extends CommissionInquiry {
    ticketId: number;
}

@Injectable()
export class TicketsService extends BaseService {
    constructor(private readonly _httpClient: HttpClient, protected readonly injector: Injector) {
        super(injector);
    }

    /**
     * Creates a Ticket
     * @param tenantId
     * @param ticketStatusId
     * @param requestorId
     * @param assignedUserId
     * @param providerTicketNumber
     * @param description
     * @param ticketPriority
     * @param ticketTypeId
     * @param departmentId
     * @param subject
     * @param ticketSourceId
     * @param messageId
     * @param customerId
     * @param locationId
     * @param productId
     * @param partnerId
     * @param providerIds
     */
    createTicket(
        tenantId: number | undefined,
        ticketStatusId: number | undefined,
        requestorId: number | undefined,
        assignedUserId: number | undefined,
        providerTicketNumber: string,
        description: string,
        ticketPriority: number,
        ticketTypeId: number | undefined,
        departmentId: number | undefined,
        subject: string,
        ticketSourceId: number,
        messageId: string,
        customerId: number | undefined,
        locationId: number | undefined,
        productId: number | undefined,
        partnerId: number | undefined,
        providerIds: number[]
    ): Observable<Ticket> {
        let url = this.environment.backendUrl + '/api/services/app/Tickets/v2/Create';

        url = url.replace(/[?&]$/, "");

        const body = {
            ticket: {
                tenantId: tenantId,
                ticketStatusId: ticketStatusId,
                requestorId: requestorId,
                assignedUserId: assignedUserId,
                providerTicketNumber: providerTicketNumber,
                description: description,
                ticketPriority: ticketPriority,
                ticketTypeId: ticketTypeId,
                departmentId: departmentId,
                subject: subject,
                ticketSourceId: ticketSourceId,
                messageId: messageId,
                customerId: customerId,
                locationId: locationId,
                productId: productId,
                partnerId: partnerId,
                providerIds: providerIds
            }
        }

        return this._httpClient
            .post<Ticket>(url, body, {})
            .pipe(
                map((response: any) => {
                    return response.result as Ticket ?? null;
                }),
                catchError(this.handleError),
            );
    }


    /**
     * Updates a Ticket
     * @param id
     * @param requestorId
     * @param providerTicketNumber
     * @param description
     * @param ticketTypeId
     * @param customerId
     * @param locationId
     * @param productId
     * @param providerIds
     */
    updateTicket(
        id: number,
        requestorId: number | undefined,
        providerTicketNumber: string,
        description: string,
        ticketTypeId: number | undefined,
        customerId: number | undefined,
        locationId: number | undefined,
        productId: number | undefined,
        providerIds: number[]
    ): Observable<Ticket> {
        let url = this.environment.backendUrl + '/api/services/app/Tickets/v2/Update';

        url = url.replace(/[?&]$/, "");

        const body = {
            ticket: {
                id: id,
                requestorId: requestorId,
                providerTicketNumber: providerTicketNumber,
                description: description,
                ticketTypeId: ticketTypeId,
                customerId: customerId,
                locationId: locationId,
                productId: productId,
                providerIds: providerIds
            }
        }

        return this._httpClient
            .put<Ticket>(url, body, {})
            .pipe(
                map((response: any) => {
                    return response.result as Ticket ?? null;
                }),
                catchError(this.handleError),
            );
    }

    /**
     * Verifies ticket commission inquiry data to determine if the ticket has commission inquiry capability
     * and thus if the UI should display the commission inquiry section.
     * @param ticketId The ID of the ticket to verify commission inquiry for
     * @returns Observable of boolean (true if ticket is of commission inquiry, false otherwise)
     */
    verifyCommissionInquiryCapability(ticketId: number): Observable<boolean> {
        let url = this.environment.backendUrl + '/api/v2/ticketCommissionInquiries';
        url += `?ticketId=${ticketId}`;

        // Add custom header to skip retries for this request.
        // Look at angular/src/shared/common/session/custom-abp-http-interceptor.ts for more details
        const headers = new HttpHeaders().set('X-Skip-Retry', 'true');

        return this._httpClient
            .head(url, { observe: 'response', headers })
            .pipe(
                map((response) => {
                    // 204 No Content means commission inquiry capability exists
                    return response.status === 204;
                }),
                catchError((error) => {
                    if (error.status === 400 || error.status === 403) {
                        return [false];
                    }
                    return this.handleError(error);
                }),
            );
    }

    /**
     * Retrieves commission inquiry data for a specific ticket
     * @param ticketId The ID of the ticket to retrieve commission inquiry for
     * @returns Observable of CommissionInquiry data
     */
    getCommissionInquiry(ticketId: number): Observable<CommissionInquiry> {
        let url = this.environment.backendUrl + '/api/v2/ticketCommissionInquiries';
        url += `?ticketId=${ticketId}`;

        const defaultValue: CommissionInquiry = {
            oneTimeSpiffCommissionTrueUp: null,
            oneTimeResidualGrossCommissionTrueUp: null,
            numberOfMonthsIncludedInTrueUp: null,
            netNewGrossCommission: null
        };

        return this._httpClient
            .get<CommissionInquiry>(url, {})
            .pipe(
                map((response: any) => {
                    // If response is already a CommissionInquiry, return it; otherwise, fallback to default
                    return (response && typeof response === 'object' && 'oneTimeSpiffCommissionTrueUp' in response)
                        ? response as CommissionInquiry
                        : defaultValue;
                }),
                catchError((error) => {
                    if (error.status === 404) {
                        return [defaultValue];
                    }
                    return this.handleError(error);
                }),
            );
    }

    /**
     * Saves commission inquiry data for a ticket
     * @param commissionInquiry The commission inquiry data to save
     * @returns Observable of the saved CommissionInquiry data
     */
    saveCommissionInquiry(commissionInquiry: CommissionInquiryInput): Observable<CommissionInquiry> {
        let url = this.environment.backendUrl + '/api/v2/ticketCommissionInquiries';

        return this._httpClient
            .put<CommissionInquiry>(url, commissionInquiry, {})
            .pipe(
                map((response: any) => {
                    return response.result as CommissionInquiry ?? null;
                }),
                catchError(this.handleError),
            );
    }
}
